<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { ChevronDownIcon, CheckIcon } from '../Icons'

const props = defineProps({
	options: {
		type: Array,
		required: true,
	},
	selected: {
		type: Array,
		required: true,
	},
	labelFn: {
		type: Function,
		required: true,
	},
	placeholder: {
		type: String,
		default: '',
	},
})

const emit = defineEmits(['change'])

const isOpen = ref(false)
const el = ref(null)

const displayText = computed(() => {
	return props.selected.length > 0
		? `${props.selected.length} selected`
		: props.placeholder
})

const toggleOption = (option) => {
	const next = props.selected.includes(option)
		? props.selected.filter(v => v !== option)
		: [...props.selected, option]

	emit('change', next)
}

const clickOutside = (e) => {
	if (el.value && !el.value.contains(e.target)) {
		isOpen.value = false
	}
}

onMounted(() => {
	document.addEventListener('mousedown', clickOutside)
})

onBeforeUnmount(() => {
	document.removeEventListener('mousedown', clickOutside)
})
</script>

<template>
	<div class="relative w-full" ref="el">
		<!-- Button -->
		<button
			type="button"
			@click="isOpen = !isOpen"
			class="flex items-center justify-between w-full h-9 px-3 bg-white border border-slate-200 rounded-md text-sm text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
		>
      <span
				class="truncate"
				:class="selected.length === 0 ? 'text-slate-400' : 'text-slate-900'"
			>
        {{ displayText }}
      </span>
			<ChevronDownIcon class="w-4 h-4 text-slate-400" />
		</button>

		<!-- Dropdown -->
		<div
			v-if="isOpen"
			class="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto animate-in fade-in zoom-in-95 duration-100"
		>
			<div
				v-for="option in options"
				:key="option"
				@click="toggleOption(option)"
				class="flex items-center px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors"
			>
				<div
					class="w-4 h-4 rounded border mr-2 flex items-center justify-center transition-colors"
					:class="selected.includes(option)
            ? 'bg-blue-600 border-blue-600'
            : 'border-slate-300 bg-white'"
				>
					<CheckIcon
						v-if="selected.includes(option)"
						class="w-3 h-3 text-white"
						stroke-width="3"
					/>
				</div>

				<span class="text-sm text-slate-700">
          {{ labelFn(option) }}
        </span>
			</div>
		</div>
	</div>
</template>
